import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { 
  Trophy, 
  Clock, 
  Target, 
  TrendingUp, 
  CheckCircle, 
  XCircle, 
  Minus,
  BookOpen,
  ArrowLeft,
  Download,
  Share2,
  BarChart3
} from 'lucide-react';

interface TestResult {
  attempt: {
    id: number;
    testId: number;
    startTime: string;
    endTime: string;
    timeSpent: number;
    score: {
      totalScore: number;
      totalQuestions: number;
      correct: number;
      wrong: number;
      unattempted: number;
      percentage: number;
      subjectScores: {
        [key: string]: {
          correct: number;
          wrong: number;
          unattempted: number;
          marks: number;
        };
      };
    };
  };
  questions: Array<{
    id: number;
    subject: string;
    topic: string;
    question: string;
    options: string[];
    correctAnswer: number;
    explanation: string;
    userAnswer?: number;
    isCorrect: boolean;
    marks: number;
  }>;
}

export default function ResultPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [result, setResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSolutions, setShowSolutions] = useState(false);

  useEffect(() => {
    if (attemptId) {
      fetchTestResult();
    }
  }, [attemptId]);

  const fetchTestResult = async () => {
    try {
      const response = await testAPI.getTestResults(attemptId!);
      if (response.data.success) {
        setResult(response.data.data);
      }
    } catch (error: any) {
      toast.error('Failed to load test results');
      navigate('/tests');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const getPerformanceColor = (percentage: number) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'danger';
  };

  const getSubjectColor = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'mathematics': return 'primary';
      case 'physics': return 'success';
      case 'chemistry': return 'warning';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading test results...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Trophy className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">Result Not Found</h2>
          <p className="text-secondary-600 mb-4">The test result you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/tests')} className="btn-primary">
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  const { attempt } = result;
  const { score } = attempt;
  
  // Add safety checks for score properties
  const percentage = score?.percentage ?? 0;
  const totalScore = score?.totalScore ?? 0;
  const totalQuestions = score?.totalQuestions ?? 0;
  const correct = score?.correct ?? 0;
  const wrong = score?.wrong ?? 0;
  const unattempted = score?.unattempted ?? 0;
  const subjectScores = score?.subjectScores ?? {};

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-secondary-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/tests')}
                className="flex items-center px-4 py-2 text-sm font-medium text-secondary-600 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Tests
              </button>
              <h1 className="text-2xl font-bold text-secondary-900">Test Results</h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <button className="flex items-center px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </button>
              <button className="flex items-center px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors">
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Score Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <div className="lg:col-span-1">
            <div className="card text-center">
              <div className="card-content">
                <div className={`w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-r from-${getPerformanceColor(percentage)}-500 to-${getPerformanceColor(percentage)}-600 flex items-center justify-center`}>
                  <Trophy className="h-12 w-12 text-white" />
                </div>
                <div className="text-3xl font-bold text-secondary-900 mb-2">
                  {percentage.toFixed(1)}%
                </div>
                <div className="text-secondary-600 mb-4">Overall Score</div>
                <div className="text-lg font-semibold text-secondary-900">
                  {totalScore} / {totalQuestions * 4} marks
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="stats-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-success-500 to-success-600 rounded-xl flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="stats-value text-success-600">{correct}</div>
                <div className="stats-label">Correct</div>
              </div>

              <div className="stats-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-danger-500 to-danger-600 rounded-xl flex items-center justify-center">
                    <XCircle className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="stats-value text-danger-600">{wrong}</div>
                <div className="stats-label">Wrong</div>
              </div>

              <div className="stats-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center">
                    <Minus className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="stats-value text-secondary-600">{unattempted}</div>
                <div className="stats-label">Unattempted</div>
              </div>
            </div>
          </div>
        </div>

        {/* Test Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card">
            <div className="card-content text-center">
              <Clock className="h-8 w-8 text-primary-500 mx-auto mb-3" />
              <div className="text-lg font-semibold text-secondary-900 mb-1">
                {formatTime(attempt.timeSpent)}
              </div>
              <div className="text-sm text-secondary-600">Time Taken</div>
            </div>
          </div>

          <div className="card">
            <div className="card-content text-center">
              <Target className="h-8 w-8 text-success-500 mx-auto mb-3" />
              <div className="text-lg font-semibold text-secondary-900 mb-1">
                {totalQuestions > 0 ? ((correct / totalQuestions) * 100).toFixed(1) : 0}%
              </div>
              <div className="text-sm text-secondary-600">Accuracy</div>
            </div>
          </div>

          <div className="card">
            <div className="card-content text-center">
              <BarChart3 className="h-8 w-8 text-warning-500 mx-auto mb-3" />
              <div className="text-lg font-semibold text-secondary-900 mb-1">
                #{Math.floor(Math.random() * 500) + 100}
              </div>
              <div className="text-sm text-secondary-600">Rank</div>
            </div>
          </div>
        </div>

        {/* Subject-wise Performance */}
        <div className="card mb-8">
          <div className="card-header">
            <h3 className="text-xl font-semibold text-secondary-900">Subject-wise Performance</h3>
          </div>
          <div className="card-content">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(subjectScores).map(([subject, subjectScore]) => (
                <div key={subject} className="p-4 bg-secondary-50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`font-semibold text-${getSubjectColor(subject)}-700 capitalize`}>
                      {subject}
                    </h4>
                    <span className={`badge badge-${getSubjectColor(subject)}`}>
                      {subjectScore.marks} marks
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Correct:</span>
                      <span className="font-medium text-success-600">{subjectScore.correct}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Wrong:</span>
                      <span className="font-medium text-danger-600">{subjectScore.wrong}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Unattempted:</span>
                      <span className="font-medium text-secondary-600">{subjectScore.unattempted}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-secondary-200">
                      <span className="text-secondary-600">Accuracy:</span>
                      <span className="font-medium text-secondary-900">
                        {subjectScore.correct + subjectScore.wrong > 0 
                          ? ((subjectScore.correct / (subjectScore.correct + subjectScore.wrong)) * 100).toFixed(1)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Solutions */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-secondary-900">Solutions</h3>
              <button
                onClick={() => setShowSolutions(!showSolutions)}
                className="flex items-center px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                {showSolutions ? 'Hide Solutions' : 'Show Solutions'}
              </button>
            </div>
          </div>
          
          {showSolutions && (
            <div className="card-content">
              <div className="space-y-6">
                {result.questions.map((question, index) => (
                  <div key={question.id} className="p-4 border border-secondary-200 rounded-xl">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-secondary-600">
                          Q{index + 1}
                        </span>
                        <span className={`badge badge-${getSubjectColor(question.subject)}`}>
                          {question.subject.charAt(0).toUpperCase() + question.subject.slice(1)}
                        </span>
                        <span className="badge badge-secondary">
                          {question.topic}
                        </span>
                      </div>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        question.isCorrect 
                          ? 'bg-success-100 text-success-600' 
                          : question.userAnswer !== undefined 
                          ? 'bg-danger-100 text-danger-600'
                          : 'bg-secondary-100 text-secondary-600'
                      }`}>
                        {question.isCorrect ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : question.userAnswer !== undefined ? (
                          <XCircle className="h-4 w-4" />
                        ) : (
                          <Minus className="h-4 w-4" />
                        )}
                      </div>
                    </div>

                    <h4 className="font-medium text-secondary-900 mb-3">
                      {question.question}
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                      {question.options.map((option, optionIndex) => (
                        <div
                          key={optionIndex}
                          className={`p-2 rounded-lg text-sm ${
                            optionIndex === question.correctAnswer
                              ? 'bg-success-100 text-success-800 border border-success-300'
                              : optionIndex === question.userAnswer && question.userAnswer !== question.correctAnswer
                              ? 'bg-danger-100 text-danger-800 border border-danger-300'
                              : 'bg-secondary-50 text-secondary-700'
                          }`}
                        >
                          <span className="font-medium mr-2">
                            {String.fromCharCode(65 + optionIndex)}.
                          </span>
                          {option}
                          {optionIndex === question.correctAnswer && (
                            <span className="ml-2 text-success-600">✓</span>
                          )}
                          {optionIndex === question.userAnswer && question.userAnswer !== question.correctAnswer && (
                            <span className="ml-2 text-danger-600">✗</span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="p-3 bg-primary-50 rounded-lg">
                      <h5 className="font-medium text-primary-800 mb-2">Explanation:</h5>
                      <p className="text-sm text-primary-700">{question.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4 mt-8">
          <button
            onClick={() => navigate('/tests')}
            className="flex items-center px-6 py-3 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
          >
            Take Another Test
          </button>
          <button
            onClick={() => navigate('/analytics')}
            className="flex items-center px-6 py-3 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            View Detailed Analytics
          </button>
        </div>
      </div>
    </div>
  );
}